'use client';

import {
  ModalDialog,
  Modal,
  ModalOverlay,
  Button,
  CloseButton,
  Form,
  LoadingSpinner,
  Alert,
  HorizontalEventCard
} from '@goodneighbor/ui';
import { cleanPhoneNumber } from '@/services/phone';
import styles from './registrationModal.module.scss';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Event } from '@goodneighbor/api/schemas/events/event.schema';
import { registerForEventRequestSchema } from '@goodneighbor/api/routes/events/register/registerEvent.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useRegisterEventMutation from '@/hooks/api/events/useRegisterEventMutation';
import { useState, useEffect } from 'react';
import VolunteerInformation from './components/VolunteerInformation';
import TermsAndConditions from './components/TermsAndConditions';
import RegistrationComplete from './components/RegistrationComplete';
import { cx } from 'class-variance-authority';
import { useMeasure } from 'react-use';
import Confetti from 'react-confetti';
import useAccountQuery from '@/hooks/useAccountQuery';
import LogInForm from '@/app/volunteer/form';
import {
  ACTION_TYPES,
  CLIENT_ERROR_MESSAGE as CAPTCHA_ERROR_MESSAGE
} from '@goodneighbor/api/services/recaptcha/constants';
import { useSearchParams } from 'next/navigation';
import { parseAsString, useQueryState } from 'nuqs';
import {
  PARTNER_LABELS,
  DEFAULT_LABELS
} from './components/VolunteerInformation/constants';
import useRegisterVolunteerForEventAsPartnerMutation from '@/hooks/api/events/useRegisterVolunteerForEventAsPartnerMutation';
import { useHasJWT } from '@/hooks/useJWT';
import { removeJWT } from '@/services/jwt';
import { parseForEventCardProps } from '@/services/event';

export type RegistrationFormData = z.infer<
  typeof registerForEventRequestSchema
>;

const registrationModalSteps = [
  'volunteerInfo',
  'terms',
  'confirmation',
  'success',
  'logIn'
] as const;

export type RegistrationModalSteps = (typeof registrationModalSteps)[number];

type RegistrationModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event;
  organizationName: string;
  defaultValues?: Partial<RegistrationFormData>;
  accountData?: ReturnType<typeof useAccountQuery>['data'];
  isSignedIn?: boolean;
  isPartnerRegistration?: boolean;
};

function RegistrationModal({
  isOpen,
  onOpenChange,
  event,
  organizationName,
  defaultValues,
  accountData,
  isSignedIn,
  isPartnerRegistration
}: RegistrationModalProps) {
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [_dobParam, setDobParam] = useQueryState(
    'dob',
    parseAsString.withDefault('')
  );
  const [_genderParam, setGenderParam] = useQueryState(
    'gender',
    parseAsString.withDefault('')
  );
  const [_orgParam, setOrgParam] = useQueryState(
    'org',
    parseAsString.withDefault('')
  );
  const [ref, { width, height }] = useMeasure<HTMLFormElement>();
  const step = registrationModalSteps[stepIndex];
  const registerEventMutation = useRegisterEventMutation({
    eventId: event.id,
    onSuccess: () => {
      setError(null);
      setStepIndex(registrationModalSteps.indexOf('success'));
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const registerVolunteerForEventAsPartnerMutation =
    useRegisterVolunteerForEventAsPartnerMutation({
      eventId: event.id,
      onSuccess: () => {
        form.reset();
        setError(null);
        onOpenChange(false);
      },
      onError: (error) => {
        setError(error.message);
      }
    });

  const form = useForm<RegistrationFormData>({
    resolver: zodResolver(registerForEventRequestSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      acceptedCancellationPolicyAt: '',
      acceptedTermsAndConditionsAt: '',
      acceptedPhoneTermsAt: '',
      prefersSpanishCommunication: false,
      ...defaultValues
    }
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setStepIndex(registrationModalSteps.indexOf('volunteerInfo'));
      setError(null);
      setDobParam('');
      setGenderParam('');
      setOrgParam('');
    }
    onOpenChange(isOpen);
  };

  const onSubmit = async (data: RegistrationFormData) => {
    window.grecaptcha.ready(async function () {
      const token = await grecaptcha.execute(
        process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY || '',
        { action: ACTION_TYPES.EVENT_REGISTRATION }
      );
      registerEventMutation.mutate({
        ...data,
        eventId: event.id,
        phone: cleanPhoneNumber(
          (!isPartnerRegistration && accountData?.data.phone) || data.phone
        ),
        birthDate: new Date(data.birthDate || '').toISOString(),
        recaptchaToken: token
      });
    });
  };

  const onPartnerSubmit = async (data: RegistrationFormData) => {
    registerVolunteerForEventAsPartnerMutation.mutate({
      ...data,
      eventId: event.id,
      phone: cleanPhoneNumber(
        (!isPartnerRegistration && accountData?.data.phone) || data.phone
      ),
      birthDate: new Date(data.birthDate || '').toISOString()
    });
  };

  const nextStep = async () => {
    let isValid = false;

    switch (step) {
      case 'volunteerInfo':
        isValid = await form.trigger([
          'firstName',
          'lastName',
          'phone',
          'email',
          'birthDate',
          'gender',
          'isInternalVolunteer',
          'acceptedCancellationPolicyAt',
          'acceptedPhoneTermsAt'
        ]);
        if (isValid) {
          setStepIndex(registrationModalSteps.indexOf('terms'));
        }
        break;
      case 'terms':
        isValid = await form.trigger(['acceptedTermsAndConditionsAt']);
        if (isValid) {
          setStepIndex(registrationModalSteps.indexOf('confirmation'));
        }
        break;
      default:
        break;
    }
  };

  const isButtonDisabled = () => {
    if (step === 'terms') {
      return !form.getValues().acceptedTermsAndConditionsAt;
    }
    return false;
  };

  const switchToLogIn = () => {
    setStepIndex(registrationModalSteps.indexOf('logIn'));
  };

  const renderContent = () => {
    switch (step) {
      case 'volunteerInfo':
        return (
          <VolunteerInformation
            event={event}
            organizationName={organizationName}
            form={form}
            disabledValues={{
              email: !!defaultValues?.email,
              firstName: !!defaultValues?.firstName,
              lastName: !!defaultValues?.lastName,
              phone: !!defaultValues?.phone,
              gender: !!defaultValues?.gender,
              birthDate: !!defaultValues?.birthDate
            }}
            isSignedIn={isSignedIn}
            onLogInClick={switchToLogIn}
            labels={isPartnerRegistration ? PARTNER_LABELS : DEFAULT_LABELS}
          />
        );
      case 'terms':
        return <TermsAndConditions form={form} />;
      case 'confirmation':
        return (
          <VolunteerInformation
            form={form}
            event={event}
            organizationName={organizationName}
            readonly
            onChange={() => {
              setStepIndex(registrationModalSteps.indexOf('volunteerInfo'));
              setError(null);
            }}
          />
        );
      case 'success':
        return (
          <RegistrationComplete
            event={event}
            organizationName={organizationName}
          />
        );
      default:
        return null;
    }
  };

  const buildCaptchaUrl = () => {
    const redirectParams = new URLSearchParams(window.location.search);
    redirectParams.append('dob', form.getValues('birthDate') ?? '');
    redirectParams.append('gender', form.getValues('gender'));
    redirectParams.append(
      'org',
      form.getValues('isInternalVolunteer') ? 'yes' : 'no'
    );
    const currentUrl =
      window.location.href.split('?')[0] + `?${redirectParams.toString()}`;
    const params = new URLSearchParams();
    params.append('redirect', encodeURIComponent(currentUrl));
    params.append('firstname', form.getValues('firstName'));
    params.append('lastname', form.getValues('lastName'));
    params.append('email', form.getValues('email'));
    params.append('phone', form.getValues('phone'));
    return `/volunteer/sign-up?${params.toString()}`;
  };

  if (step === 'logIn') {
    return (
      <ModalOverlay
        className={styles.overlay}
        isDismissable
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Modal className={styles.modal}>
          <ModalDialog className={styles.modalDialog}>
            <div className={cx(styles.registrationModal)}>
              <div className={styles.header}>
                <h1 className={styles.title}>Sign In</h1>
                <CloseButton
                  size="lg"
                  onClick={() => handleOpenChange(false)}
                  className={styles.headerCloseButton}
                />
              </div>
              <div className={cx(styles.content, styles.logInForm)}>
                <LogInForm />
              </div>
            </div>
          </ModalDialog>
        </Modal>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay
      className={styles.overlay}
      isDismissable
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Modal className={styles.modal}>
        {() => (
          <ModalDialog className={styles.modalDialog}>
            {() => (
              <Form {...form}>
                <form
                  ref={ref}
                  className={cx(
                    styles.registrationModal,
                    step === 'success' && styles.success
                  )}
                  onSubmit={form.handleSubmit(onSubmit)}
                >
                  <Confetti
                    width={width}
                    height={height}
                    run={step === 'success'}
                    recycle={false}
                  />
                  <div
                    className={cx(
                      styles.header,
                      step === 'success' && styles.noBorder
                    )}
                  >
                    {step !== 'success' && (
                      <HorizontalEventCard
                        isCompact
                        {...parseForEventCardProps(event, organizationName)}
                        className={styles.eventCard}
                      />
                    )}
                    <h1 className={styles.title}>
                      {step === 'success'
                        ? 'Registration Complete!'
                        : 'Event Registration'}
                    </h1>
                    <CloseButton
                      size="lg"
                      onClick={() => handleOpenChange(false)}
                      className={styles.headerCloseButton}
                    />
                  </div>
                  <div
                    className={cx(
                      styles.content,
                      step === 'success' && styles.contentSuccess
                    )}
                  >
                    {renderContent()}
                    {error && (
                      <Alert
                        color="error"
                        title="There was a problem"
                        showCloseButton
                        description={
                          error === CAPTCHA_ERROR_MESSAGE ? (
                            <>
                              You must{' '}
                              <Button
                                href={buildCaptchaUrl()}
                                color="link-color"
                                size="xs"
                              >
                                sign up
                              </Button>{' '}
                              to register for an event.
                            </>
                          ) : (
                            error
                          )
                        }
                        onClose={() => setError(null)}
                      />
                    )}
                  </div>
                  {step !== 'success' && (
                    <div className={styles.footer}>
                      {step === 'confirmation' && (
                        <div className={styles.captchaDisclaimer}>
                          This site is protected by reCAPTCHA and the Google{' '}
                          <Button
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="link-color"
                            size="xs"
                          >
                            Privacy Policy
                          </Button>{' '}
                          and{' '}
                          <Button
                            href="https://policies.google.com/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="link-color"
                            size="xs"
                          >
                            Terms of Service
                          </Button>{' '}
                          apply.
                        </div>
                      )}
                      <Button
                        isDisabled={isButtonDisabled()}
                        isLoading={
                          registerEventMutation.isPending ||
                          registerVolunteerForEventAsPartnerMutation.isPending
                        }
                        type="button"
                        onClick={() => {
                          if (isPartnerRegistration) {
                            form.setValue(
                              'acceptedTermsAndConditionsAt',
                              new Date().toISOString()
                            );
                            form.handleSubmit(onPartnerSubmit)();
                            return;
                          }
                          if (step !== 'confirmation') {
                            nextStep();
                          } else {
                            form.handleSubmit(onSubmit)();
                          }
                        }}
                        className={styles.submitButton}
                        size="lg"
                      >
                        {step !== 'confirmation'
                          ? isPartnerRegistration
                            ? 'Register Volunteer'
                            : 'Continue to confirmation'
                          : 'Submit registration'}
                      </Button>
                    </div>
                  )}
                </form>
              </Form>
            )}
          </ModalDialog>
        )}
      </Modal>
    </ModalOverlay>
  );
}

export default function Wrapper(props: RegistrationModalProps) {
  const hasJWT = useHasJWT();
  const { data, isLoading, isError } = useAccountQuery({
    enabled: hasJWT
  });
  const params = useSearchParams();

  useEffect(() => {
    if (isError && hasJWT) {
      removeJWT();
    }
  }, [hasJWT, isError]);

  if (props.isOpen && hasJWT && isLoading && !isError && !data) {
    return (
      <ModalOverlay isOpen={props.isOpen} className={styles.overlay}>
        <Modal>
          <ModalDialog>
            <LoadingSpinner />
          </ModalDialog>
        </Modal>
      </ModalOverlay>
    );
  }

  const defaultValues = props.isPartnerRegistration
    ? {}
    : {
        firstName: data?.data.firstName || '',
        lastName: data?.data.lastName || '',
        email: data?.data.email || '',
        phone: data?.data.phone.split('+1')[1] || '',
        birthDate: data?.data.birthDate || params.get('dob') || '',
        gender: data?.data.gender || params.get('gender') || undefined,
        isInternalVolunteer:
          params.get('org') === 'yes'
            ? true
            : params.get('org') === 'no'
              ? false
              : undefined
      };

  return (
    <RegistrationModal
      {...props}
      isSignedIn={!!data}
      accountData={data}
      defaultValues={defaultValues}
    />
  );
}
